import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Filter, Calendar, AlertCircle, ChevronRight, ChevronLeft, Loader2, LogOut, FileText, X, Video, Image } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// PDF URLs - served via backend proxy to avoid CORS issues
const SEO_REPORT_PDF_URL = `${BACKEND_URL}/api/seo-report`;
const TESTING_SCOPE_PDF_URL = `${BACKEND_URL}/api/testing-scope`;

// Create axios instance with auth header
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

const BugListPage = () => {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState(""); // For debouncing
  const [statusFilter, setStatusFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBugs, setTotalBugs] = useState(0);
  const [pageSize] = useState(10);
  
  // PDF Modal States
  const [seoReportOpen, setSeoReportOpen] = useState(false);
  const [testingScopeOpen, setTestingScopeOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(true);
  
  const navigate = useNavigate();
  const username = localStorage.getItem("username");

  const openSeoReport = () => {
    setPdfLoading(true);
    setSeoReportOpen(true);
    // Hide loading after a short delay since iframe onLoad doesn't work well with PDFs
    setTimeout(() => setPdfLoading(false), 2000);
  };

  const openTestingScope = () => {
    setPdfLoading(true);
    setTestingScopeOpen(true);
    // Hide loading after a short delay since iframe onLoad doesn't work well with PDFs
    setTimeout(() => setPdfLoading(false), 2000);
  };

  const fetchBugs = useCallback(async (page = 1, search = "", status = "all", severity = "all") => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });
      
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      if (status && status !== 'all') {
        params.append('status', status);
      }
      if (severity && severity !== 'all') {
        params.append('severity', severity);
      }
      
      const response = await axios.get(`${API}/bugs?${params.toString()}`, getAuthHeaders());
      setBugs(response.data.bugs);
      setCurrentPage(response.data.page);
      setTotalPages(response.data.totalPages);
      setTotalBugs(response.data.total);
      setError(null);
    } catch (err) {
      console.error("Error fetching bugs:", err);
      if (err.response?.status === 401) {
        handleLogout();
        return;
      }
      setError("Failed to load bug reports. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  // Fetch bugs when page, search, or filters change
  useEffect(() => {
    fetchBugs(currentPage, searchQuery, statusFilter, severityFilter);
  }, [currentPage, searchQuery, statusFilter, severityFilter, fetchBugs]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
        setCurrentPage(1); // Reset to first page when search changes
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchInput, searchQuery]);

  // Reset to first page when filters change
  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleSeverityChange = (value) => {
    setSeverityFilter(value);
    setCurrentPage(1);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    toast.info("Logged out successfully");
    navigate("/login");
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      if (currentPage <= 3) {
        endPage = Math.min(4, totalPages - 1);
      }
      if (currentPage >= totalPages - 2) {
        startPage = Math.max(2, totalPages - 3);
      }
      
      if (startPage > 2) {
        pages.push('...');
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const getSeverityBadgeClass = (severity) => {
    const classes = {
      "Critical": "bg-red-200 text-red-900 border-red-300",
      "High": "bg-red-100 text-red-800 border-red-200",
      "Medium": "bg-yellow-100 text-yellow-800 border-yellow-200",
      "Low": "bg-blue-100 text-blue-800 border-blue-200",
      "Enhancement": "bg-purple-100 text-purple-800 border-purple-200",
      "UI/UX Suggestion": "bg-pink-100 text-pink-800 border-pink-200",
    };
    return classes[severity] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      "Open": "bg-green-100 text-green-800 border-green-200",
      "In Progress": "bg-blue-100 text-blue-800 border-blue-200",
      "Closed": "bg-gray-100 text-gray-800 border-gray-200",
      "Not a Bug": "bg-orange-100 text-orange-800 border-orange-200",
    };
    return classes[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Predefined filter options (matching Airtable values - in Airtable's defined order)
  const statusOptions = ["Open", "In Progress", "Closed", "Not a Bug"];
  const severityOptions = ["Critical", "High", "Medium", "Low", "Enhancement", "UI/UX Suggestion"];

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/bugs" className="flex items-center gap-2">
            <img
              src="https://www.shoperator.ai/assets/shoperator_logo-BmLJu6La.svg"
              alt="Shoperator"
              className="h-8"
              data-testid="logo"
            />
          </Link>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={openSeoReport}
              className="text-[#4DB43E] border-[#4DB43E] hover:bg-[#4DB43E] hover:text-white rounded-full"
              data-testid="seo-report-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              View SEO Report
            </Button>
            <Button
              variant="outline"
              onClick={openTestingScope}
              className="text-[#4DB43E] border-[#4DB43E] hover:bg-[#4DB43E] hover:text-white rounded-full"
              data-testid="testing-scope-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Testing Scope
            </Button>
            <span className="text-sm text-gray-600">
              Welcome, <span className="font-medium text-gray-900">{username}</span>
            </span>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-gray-600 hover:text-gray-900 rounded-full"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* SEO Report PDF Modal */}
      <Dialog open={seoReportOpen} onOpenChange={setSeoReportOpen}>
        <DialogContent className="max-w-[95vw] w-full max-h-[95vh] h-full p-0 bg-white border-none overflow-hidden [&>button]:hidden">
          <div className="relative w-full h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-3 bg-gray-100 border-b">
              <DialogTitle className="text-gray-900 text-lg font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#4DB43E]" />
                SEO Analysis Report
              </DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSeoReportOpen(false)}
                className="text-gray-600 hover:bg-gray-200 rounded-full"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* PDF Container */}
            <div className="flex-1 relative bg-white overflow-hidden flex flex-col">
              {/* Loading Spinner - shows briefly */}
              {pdfLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 animate-spin text-[#4DB43E]" />
                    <span className="text-gray-600">Loading PDF...</span>
                  </div>
                </div>
              )}

              {/* PDF Viewer using iframe - works in most modern browsers */}
              <iframe
                src={`${SEO_REPORT_PDF_URL}?token=${localStorage.getItem("token")}`}
                title="SEO Analysis Report"
                className="flex-1 w-full border-none"
                style={{ minHeight: 'calc(95vh - 120px)' }}
              />

              {/* Bottom action bar */}
              <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
                <span className="text-sm text-gray-500">
                  If the PDF doesn't display, click the button to open in a new tab
                </span>
                <a 
                  href={`${SEO_REPORT_PDF_URL}?token=${localStorage.getItem("token")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#4DB43E] hover:bg-[#3ea032] text-white px-5 py-2 rounded-full font-medium transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  Open PDF in New Tab
                </a>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Testing Scope PDF Modal */}
      <Dialog open={testingScopeOpen} onOpenChange={setTestingScopeOpen}>
        <DialogContent className="max-w-[95vw] w-full max-h-[95vh] h-full p-0 bg-white border-none overflow-hidden [&>button]:hidden">
          <div className="relative w-full h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-3 bg-gray-100 border-b">
              <DialogTitle className="text-gray-900 text-lg font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#4DB43E]" />
                Testing Scope
              </DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTestingScopeOpen(false)}
                className="text-gray-600 hover:bg-gray-200 rounded-full"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* PDF Container */}
            <div className="flex-1 relative bg-white overflow-hidden flex flex-col">
              {/* Loading Spinner - shows briefly */}
              {pdfLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 animate-spin text-[#4DB43E]" />
                    <span className="text-gray-600">Loading PDF...</span>
                  </div>
                </div>
              )}

              {/* PDF Viewer using iframe - works in most modern browsers */}
              <iframe
                src={`${TESTING_SCOPE_PDF_URL}?token=${localStorage.getItem("token")}`}
                title="Testing Scope"
                className="flex-1 w-full border-none"
                style={{ minHeight: 'calc(95vh - 120px)' }}
              />

              {/* Bottom action bar */}
              <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
                <span className="text-sm text-gray-500">
                  If the PDF doesn't display, click the button to open in a new tab
                </span>
                <a 
                  href={`${TESTING_SCOPE_PDF_URL}?token=${localStorage.getItem("token")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#4DB43E] hover:bg-[#3ea032] text-white px-5 py-2 rounded-full font-medium transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  Open PDF in New Tab
                </a>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Title */}
        <div className="mb-8 animate-slide-up">
          <h1
            className="text-3xl font-bold text-gray-900 text-heading"
            data-testid="page-title"
          >
            Prospera Soft Findings
          </h1>
        </div>

        {/* Filters */}
        <div
          className="flex flex-wrap gap-4 items-center justify-between bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6"
          data-testid="filters-section"
        >
          <div className="flex-1 min-w-[200px] max-w-md relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by Bug ID or Title..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10 rounded-lg border-gray-300 focus:ring-[#4DB43E] focus:border-[#4DB43E]"
              data-testid="search-input"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <Select value={statusFilter} onValueChange={handleStatusChange}>
                <SelectTrigger
                  className="w-[150px] rounded-lg"
                  data-testid="status-filter"
                >
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {statusOptions.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Select value={severityFilter} onValueChange={handleSeverityChange}>
              <SelectTrigger
                className="w-[150px] rounded-lg"
                data-testid="severity-filter"
              >
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                {severityOptions.map((severity) => (
                  <SelectItem key={severity} value={severity}>
                    {severity}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bug List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#4DB43E]" />
            <span className="ml-3 text-gray-600">Loading bug reports...</span>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-700 font-medium">{error}</p>
            <Button
              onClick={() => fetchBugs(currentPage, searchQuery, statusFilter, severityFilter)}
              className="mt-4 bg-red-600 hover:bg-red-700 text-white rounded-full"
            >
              Try Again
            </Button>
          </div>
        ) : bugs.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
            <img
              src="https://images.pexels.com/photos/25626445/pexels-photo-25626445.jpeg"
              alt="No results"
              className="w-32 h-32 object-cover rounded-full mx-auto mb-6 opacity-50"
            />
            <p className="text-gray-600 font-medium">No bug reports found</p>
            <p className="text-gray-400 text-sm mt-1">
              Try adjusting your search or filters
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4" data-testid="bug-list">
            {bugs.map((bug, index) => (
              <div
                key={bug.id}
                onClick={() => navigate(`/bugs/${bug.id}`)}
                className="bg-white border border-gray-200 rounded-xl p-6 cursor-pointer bug-card hover:border-[#4DB43E]/50 hover:shadow-lg transition-all animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`bug-card-${bug.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-sm font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        {bug.bugId || bug.id.slice(0, 8)}
                      </span>
                      {bug.severity && (
                        <Badge
                          className={`${getSeverityBadgeClass(bug.severity)} border text-xs`}
                          data-testid={`severity-badge-${bug.id}`}
                        >
                          {bug.severity}
                        </Badge>
                      )}
                      {bug.status && (
                        <Badge
                          className={`${getStatusBadgeClass(bug.status)} border text-xs`}
                          data-testid={`status-badge-${bug.id}`}
                        >
                          {bug.status}
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 text-heading group-hover:text-[#4DB43E] transition-colors">
                      {bug.title || "Untitled Bug"}
                    </h3>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
                      {bug.modulePage && (
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#4DB43E]" />
                          {bug.modulePage}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(bug.createdTime)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Thumbnail/Media Preview - Right Side */}
                  {(bug.screenshots?.length > 0 || bug.videoUrl) && (
                    <div className="flex-shrink-0 flex items-center gap-2">
                      {/* Video Icon */}
                      {bug.videoUrl && (
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center" title="Has video">
                          <Video className="w-5 h-5 text-red-600" />
                        </div>
                      )}
                      {/* Screenshot Thumbnail */}
                      {bug.screenshots?.length > 0 && (
                        <div className="relative">
                          <img
                            src={bug.screenshots[0].url}
                            alt="Bug screenshot"
                            className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                          />
                          {bug.screenshots.length > 1 && (
                            <span className="absolute -top-1 -right-1 bg-gray-800 text-white text-xs px-1.5 py-0.5 rounded-full">
                              +{bug.screenshots.length - 1}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Results count and Pagination */}
        {!loading && !error && bugs.length > 0 && (
          <div className="mt-8 space-y-4">
            {/* Results count */}
            <p className="text-sm text-gray-500 text-center">
              Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalBugs)} of {totalBugs} bug reports
            </p>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2" data-testid="pagination">
                {/* Previous Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 rounded-lg border-gray-300 disabled:opacity-50"
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>

                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {getPageNumbers().map((pageNum, index) => (
                    pageNum === '...' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-gray-400">...</span>
                    ) : (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-lg ${
                          currentPage === pageNum 
                            ? 'bg-[#4DB43E] hover:bg-[#3ea032] text-white' 
                            : 'border-gray-300 hover:border-[#4DB43E] hover:text-[#4DB43E]'
                        }`}
                        data-testid={`page-${pageNum}-btn`}
                      >
                        {pageNum}
                      </Button>
                    )
                  ))}
                </div>

                {/* Next Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 rounded-lg border-gray-300 disabled:opacity-50"
                  data-testid="next-page-btn"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default BugListPage;
